/**
 * Integrador Platform-Lifecycle
 * Conecta os sistemas de plataforma e ciclo de vida
 */

import { platformServiceFactory } from '../../platform/platform_service_factory';
import { SessionManager } from './services/session-manager';
import { ConnectivityService } from './services/connectivity-service';
import { ErrorReporter } from './services/error-reporter';
import { TaskScheduler } from './services/task-scheduler';

export class LifecyclePlatformIntegrator {
  private static isInitialized = false;
  private static networkUnsubscribe: (() => void) | null = null;

  /**
   * Inicializa a integração entre platform e lifecycle
   */
  static async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      console.log('🔗 Inicializando integração Platform-Lifecycle...');

      // Inicializa sistemas de plataforma primeiro
      await platformServiceFactory.initializeAll();

      // Inicializa sistemas de lifecycle com adaptações baseadas na plataforma
      await this.initializeLifecycleWithPlatform();

      // Configura integrações
      this.setupNetworkIntegration();
      this.setupFeatureIntegration();
      this.setupErrorIntegration();

      this.isInitialized = true;
      console.log('✅ Integração Platform-Lifecycle inicializada');

    } catch (error) {
      console.error('❌ Erro na integração Platform-Lifecycle:', error);
      throw error;
    }
  }

  /**
   * Inicializa lifecycle com configurações baseadas na plataforma
   */
  private static async initializeLifecycleWithPlatform(): Promise<void> {
    const platformService = platformServiceFactory.getService('PlatformDetection');
    const networkService = platformServiceFactory.getService('Network');
    const platformInfo = platformService.getPlatformInfo();
    const networkInfo = networkService.getNetworkInfo();
    
    // Configurações baseadas na plataforma
    const sessionTimeoutMinutes = platformInfo.isMobile ? 10 : 15; // Mobile timeout menor
    const errorReportingEnabled = true;

    // Inicializa SessionManager
    SessionManager.initialize({
      sessionTimeoutMinutes,
      onSessionTimeout: () => {
        console.log('⏰ Sessão expirou devido à inatividade');
        console.log('📊 Session timeout:', {
          platform: platformInfo.type,
          browser: platformInfo.browser,
          timeout: sessionTimeoutMinutes
        });
      }
    });

    // Inicializa ConnectivityService
    ConnectivityService.initialize();

    // Inicializa ErrorReporter
    ErrorReporter.initialize();

    console.log('🎯 Lifecycle inicializado com configurações da plataforma:', {
      sessionTimeout: sessionTimeoutMinutes,
      errorReporting: errorReportingEnabled,
      networkOnline: networkInfo.isOnline
    });
  }

  /**
   * Configura integração com eventos de rede
   */
  private static setupNetworkIntegration(): void {
    const networkService = platformServiceFactory.getService('Network');
    
    this.networkUnsubscribe = networkService.addListener((event) => {
      switch (event.type) {
        case 'online':
          console.log('🌐 Conexão restaurada');
          console.log('📶 Conectividade restaurada');
          
          // Verificar se Service Worker está disponível
          const featureService = platformServiceFactory.getService('Feature');
          if (featureService.isSupported('serviceWorkers')) {
            console.log('🔄 Service Worker disponível para tarefas em background');
          }
          break;

        case 'offline':
          console.log('📡 Conexão perdida');
          console.log('❌ Conectividade perdida');
          console.log('⏸️ Pausando tarefas que dependem de rede');
          break;

        case 'change':
          console.log('📶 Qualidade da conexão mudou:', event.info.effectiveType);
          
          if (networkService.isSlowConnection()) {
            console.log('🐌 Conexão lenta detectada - modo economia ativo');
          } else {
            console.log('🚀 Conexão rápida detectada - modo normal ativo');
          }
          break;
      }
    });
  }

  /**
   * Configura integração baseada em features disponíveis
   */
  private static setupFeatureIntegration(): void {
    const featureService = platformServiceFactory.getService('Feature');

    // Push Notifications
    if (featureService.isSupported('notifications')) {
      console.log('🔔 Notificações disponíveis');
    }

    // Service Worker para tarefas em background
    if (featureService.isSupported('serviceWorkers')) {
      console.log('🔄 Service Worker disponível para tarefas em background');
    }

    // Persistent Storage
    if (featureService.isSupported('persistentStorage')) {
      console.log('💾 Storage persistente disponível');
    }

    // Geolocation
    if (featureService.isSupported('geolocation')) {
      console.log('📍 Geolocalização disponível');
    }

    // Camera/Microphone
    if (featureService.isSupported('camera')) {
      console.log('📷 Câmera disponível');
    }

    if (featureService.isSupported('microphone')) {
      console.log('🎤 Microfone disponível');
    }

    // Fullscreen
    if (featureService.isSupported('fullscreen')) {
      console.log('🖥️ API Fullscreen disponível');
    }

    // Vibration (mobile)
    if (featureService.isSupported('vibration')) {
      console.log('📳 API de vibração disponível');
    }

    // Web Share
    if (featureService.isSupported('webShare')) {
      console.log('📤 Web Share API disponível');
    }

    // Clipboard
    if (featureService.isSupported('clipboard')) {
      console.log('📋 Clipboard API disponível');
    }
  }

  /**
   * Configura integração de relatórios de erro
   */
  private static setupErrorIntegration(): void {
    const platformService = platformServiceFactory.getService('PlatformDetection');
    const networkService = platformServiceFactory.getService('Network');
    const featureService = platformServiceFactory.getService('Feature');
    
    const platformInfo = platformService.getPlatformInfo();
    const networkInfo = networkService.getNetworkInfo();
    const featureReport = featureService.generateFeatureReport();
    
    console.log('📝 Sistema de relatórios configurado');
    console.log('🖥️ Informações de plataforma para relatórios:', {
      browser: platformInfo.browser,
      version: platformInfo.version,
      type: platformInfo.type,
      isMobile: platformInfo.isMobile,
      supportedFeatures: featureReport.supported.length,
      networkType: networkInfo.type,
      networkSpeed: networkInfo.effectiveType
    });

    console.log('🔄 Monitoramento de mudanças de plataforma ativo');
  }

  /**
   * Obtém status da integração
   */
  static getIntegrationStatus(): {
    isInitialized: boolean;
    platformReady: boolean;
    lifecycleReady: boolean;
    networkMonitoring: boolean;
    errorReporting: boolean;
    activeSessions: number;
    backgroundTasks: number;
  } {
    const networkService = platformServiceFactory.hasService('Network') 
      ? platformServiceFactory.getService('Network') 
      : null;

    return {
      isInitialized: this.isInitialized,
      platformReady: platformServiceFactory.allInitialized,
      lifecycleReady: SessionManager.isSessionActive,
      networkMonitoring: networkService?.isOnline() ?? false,
      errorReporting: true,
      activeSessions: SessionManager.isSessionActive ? 1 : 0,
      backgroundTasks: 0
    };
  }

  /**
   * Cleanup - limpa todas as integrações
   */
  static cleanup(): void {
    if (this.networkUnsubscribe) {
      this.networkUnsubscribe();
      this.networkUnsubscribe = null;
    }

    SessionManager.dispose();
    console.log('🧹 Serviços de lifecycle limpos');
    
    platformServiceFactory.cleanup();

    this.isInitialized = false;
    console.log('🧹 Integração Platform-Lifecycle limpa');
  }

  /**
   * Força sincronização entre sistemas
   */
  static async syncSystems(): Promise<void> {
    if (!this.isInitialized) return;

    try {
      const networkService = platformServiceFactory.getService('Network');
      
      // Atualiza informações de rede
      const networkInfo = networkService.getNetworkInfo();
      
      // Atualiza configurações baseadas na rede
      if (networkService.isSlowConnection()) {
        console.log('🐌 Modo economia de dados ativado');
      } else {
        console.log('🚀 Modo normal ativado');
      }

      // Testa conectividade
      const connectivityTest = await networkService.testConnectivity();
      if (!connectivityTest.isReachable) {
        console.log('❌ Conectividade perdida durante sync');
      }

      console.log('🔄 Sistemas sincronizados');
    } catch (error) {
      console.error('❌ Erro na sincronização:', error);
      console.error('📊 Erro de sync:', error instanceof Error ? error.message : 'Erro desconhecido');
    }
  }
}
